import {Choice} from 'platform/components';

import {isNotEmpty, isNotNil} from 'ramda';

import {useListRentalCalendarVehiclesQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface RentalCalendarVehicleSelectProps extends RequiredTestIdProps {
  value: string | null;
  /** The vehicle `fallbackTitle` names. Paired so the title cannot drift onto another vehicle */
  fallbackId?: string;
  /** `Choice` resolves its label out of the options, so a vehicle it lacks would read as empty */
  fallbackTitle?: string;
  onChange: (vehicleId: string) => void;
}

export function RentalCalendarVehicleSelect(props: RentalCalendarVehicleSelectProps) {
  const {data: vehicles, isLoading, isError} = useListRentalCalendarVehiclesQuery({});

  const vehicleOptions = (vehicles ?? []).map((vehicle) => ({
    value: vehicle.vehicleId,
    label:
      isNotNil(vehicle.registrationPlate) && isNotEmpty(vehicle.registrationPlate)
        ? `${vehicle.title} · ${vehicle.registrationPlate}`
        : vehicle.title,
  }));

  const options =
    isNotNil(props.fallbackId) &&
    isNotNil(props.fallbackTitle) &&
    isNotEmpty(props.fallbackTitle) &&
    props.value === props.fallbackId &&
    !vehicleOptions.some((option) => option.value === props.fallbackId)
      ? [{value: props.fallbackId, label: props.fallbackTitle}, ...vehicleOptions]
      : vehicleOptions;

  return (
    <Choice
      label={i18n.t('general.labels.vehicle')}
      value={props.value}
      options={options}
      // `isNotClearable` makes null unreachable, so it is collapsed here, not in every consumer
      onChange={(vehicleId) => {
        if (isNotNil(vehicleId)) {
          props.onChange(vehicleId);
        }
      }}
      isLoading={isLoading}
      // A failed fleet list must not take the chosen vehicle off screen with it - the fallback
      // option keeps it readable while the control itself goes inert
      isDisabled={isError}
      errorMessage={isError ? i18n.t('general.labels.somethingWentWrong') : undefined}
      isRequired
      isNotClearable
      menuInPortal
      data-testid={suffixTestId('vehicle', props)}
    />
  );
}
